(() => {
  const MODEL_GROUPS = {
    "Sedan & Avant": ["A4 / Allroad", "A5", "A6 / Allroad", "A7", "A8"],
    "Q models": ["Q5", "Q7", "Q8"],
    "S models": ["S3", "S4", "S5", "S6", "S7", "S8", "SQ5", "SQ7", "SQ8"],
    "RS models": ["RS3", "RS5", "RS6", "RS7", "RSQ8"],
    "Electric": ["e-tron", "Q4 e-tron", "Q8 e-tron", "SQ8 e-tron", "e-tron GT", "S e-tron GT", "RS e-tron GT"],
    "Other platforms": ["Lamborghini Urus SE"],
  };

  const YEARS = [2019, 2020, 2021, 2022, 2023, 2024, 2025, 2026];
  const URUS_YEARS = [2025, 2026];
  const HEADLIGHTS = {
    matrixHd: ["Matrix LED", "HD Matrix LED"],
    hdOnly: ["HD Matrix LED"],
    hdLaser: ["HD Matrix LED", "Matrix LED with laser"],
    matrixHdLaser: ["Matrix LED", "HD Matrix LED", "Matrix LED with laser"],
    hdDigital: ["HD Matrix LED", "Digital Matrix LED"],
  };

  const sets = {
    matrixHd: new Set(["A6 / Allroad", "S6"]),
    matrixHdLaser: new Set(["A5", "S5", "RS5"]),
    hdOnly: new Set(["A4 / Allroad", "S4", "Lamborghini Urus SE", "Q4 e-tron", "e-tron GT", "S e-tron GT", "RS e-tron GT"]),
    noLaser: new Set(["Q5", "SQ5", "S3", "RS3", "Lamborghini Urus SE"]),
    electricNoLaser: new Set(["e-tron", "Q8 e-tron", "SQ8 e-tron"]),
    noIncluded: new Set(["Lamborghini Urus SE"]),
    crossing: new Set(["A6 / Allroad", "S6", "RS6", "A7", "S7", "RS7", "Q7", "SQ7", "Q8", "SQ8", "RSQ8", "A8", "S8"]),
    recall: new Set(["A6 / Allroad", "A7", "A8", "e-tron", "e-tron GT", "Q7", "Q8", "Q8 e-tron", "RS6", "RS7", "RS e-tron GT", "RSQ8", "S6", "S7", "S8", "S e-tron GT", "SQ7", "SQ8", "SQ8 e-tron"]),
    maneuver: new Set(["A6 / Allroad", "S6", "RS6", "A7", "S7", "RS7", "A8", "S8", "Q7", "SQ7", "Q8", "SQ8", "RSQ8", "e-tron", "Q8 e-tron", "SQ8 e-tron"]),
    maneuverAssist: new Set(["A6 / Allroad", "A7", "A8", "S6", "S7", "S8", "RS6", "RS7", "RSQ8", "Q7", "SQ7", "Q8", "SQ8", "e-tron", "Q8 e-tron", "SQ8 e-tron"]),
    laneLight: new Set(["A8", "S8", "e-tron", "Q8 e-tron", "SQ8 e-tron"]),
  };

  const addonMeta = {
    laser: {
      label: "Blue Laser",
      copy: "Enables the factory blue visual accent in compatible laser headlight assemblies.",
    },
    laneLight: {
      label: "Lane Light",
      copy: "Projects the supported Digital Matrix light carpet within the driving lane.",
    },
    maneuverLight: {
      label: "Maneuver Light",
      copy: "Turns on the exterior door-handle lights when reverse is selected.",
    },
    maneuverAssist: {
      label: "Maneuver Assist",
      copy: "Adds supported low-speed collision intervention using factory parking hardware.",
    },
    sfd2: {
      label: "SFD2 protected-module access",
      copy: "Required for supported 2026 S3 and RS3 control modules.",
      required: true,
    },
  };

  const includedMeta = {
    crossing: { label: "Route-aware crossing light", copy: "Intersection lighting request where supported." },
    singleSide: { label: "Single-side parking light", copy: "One-side parking illumination behaviour." },
    sideMarker: { label: "Amber side-marker adjustment", copy: "Regional marker-light configuration request." },
    acceleration: { label: "Acceleration measurement", copy: "Factory performance screen where available." },
    lapTimer: { label: "Lap timer", copy: "Factory instrument-cluster timer where available." },
  };

  function init(root) {
    if (root.dataset.initialized === "true") return;
    root.dataset.initialized = "true";

    const $ = (selector) => root.querySelector(selector);
    const modelSelect = $("[data-model-select]");
    const yearSelect = $("[data-year-select]");
    const hardwareGroup = $("[data-hardware-group]");
    const hardwareOptions = $("[data-hardware-options]");
    const hardwareHint = $("[data-hardware-hint]");
    const addonGroup = $("[data-addon-group]");
    const addonOptions = $("[data-addon-options]");
    const addonHint = $("[data-addon-hint]");
    const includedWrap = $("[data-included-wrap]");
    const includedOptions = $("[data-included-options]");
    const recallStage = $("[data-recall-stage]");
    const recallSelect = $("[data-recall-select]");
    const cableReason = $("[data-cable-reason]");
    const checkoutButton = $("[data-checkout-button]");
    const checkoutNote = $("[data-checkout-note]");

    const currency = root.dataset.currency || "USD";
    const formatter = new Intl.NumberFormat(document.documentElement.lang || "en-US", {
      style: "currency",
      currency,
    });
    const money = (cents) => formatter.format(Number(cents || 0) / 100);

    const products = {
      base: { id: root.dataset.baseId, price: Number(root.dataset.basePrice) },
      laser: { id: root.dataset.laserId, price: Number(root.dataset.laserPrice) },
      laneLight: { id: root.dataset.laneLightId, price: Number(root.dataset.laneLightPrice) },
      maneuverLight: { id: root.dataset.maneuverLightId, price: Number(root.dataset.maneuverLightPrice) },
      maneuverAssist: { id: root.dataset.maneuverAssistId, price: Number(root.dataset.maneuverAssistPrice) },
      sfd2: { id: root.dataset.sfd2Id, price: Number(root.dataset.sfd2Price) },
    };

    function fillSelect(select, values, placeholder) {
      select.innerHTML = "";
      select.append(new Option(placeholder, ""));
      values.forEach((value) => select.append(new Option(value, value)));
    }

    Object.entries(MODEL_GROUPS).forEach(([label, models]) => {
      const group = document.createElement("optgroup");
      group.label = label;
      models.forEach((model) => group.append(new Option(model, model)));
      modelSelect.append(group);
    });

    function selectedHeadlight() {
      return hardwareOptions.querySelector("input:checked")?.value || "";
    }

    function headlightOptions(model, year) {
      if (!model) return [];
      if (sets.noLaser.has(model)) return HEADLIGHTS.hdOnly;
      if (model === "A8" || model === "S8") return year && year <= 2021 ? HEADLIGHTS.hdLaser : HEADLIGHTS.hdDigital;
      if (model === "Q8" || model === "SQ8") return year && year <= 2023 ? HEADLIGHTS.hdOnly : HEADLIGHTS.hdLaser;
      if (model === "RSQ8") return year && year <= 2024 ? HEADLIGHTS.hdOnly : HEADLIGHTS.hdLaser;
      if (sets.hdOnly.has(model)) return HEADLIGHTS.hdOnly;
      if (sets.matrixHd.has(model)) return HEADLIGHTS.matrixHd;
      if (sets.matrixHdLaser.has(model)) return HEADLIGHTS.matrixHdLaser;
      if (sets.electricNoLaser.has(model)) return HEADLIGHTS.hdDigital;
      return HEADLIGHTS.hdLaser;
    }

    function optionRow(type, key, label, copy, price, checked = false, disabled = false) {
      const row = document.createElement("label");
      row.className = "option-row";
      row.innerHTML = `
        <input type="${type}" name="${type === "radio" ? "headlight" : `option_${key}`}" value="${key}" ${checked ? "checked" : ""} ${disabled ? "disabled" : ""}>
        <span><strong>${label}</strong>${copy ? `<small>${copy}</small>` : ""}</span>
        <b>${price}</b>
      `;
      return row;
    }

    function renderYears() {
      const model = modelSelect.value;
      fillSelect(yearSelect, model === "Lamborghini Urus SE" ? URUS_YEARS : model ? YEARS : [], model ? "Choose a year" : "Choose the model first");
      yearSelect.disabled = !model;
    }

    function renderRecall() {
      const visible = sets.recall.has(modelSelect.value);
      recallStage.hidden = !visible;
      recallSelect.required = visible;
      if (!visible) recallSelect.value = "";
    }

    function renderHardware() {
      const options = headlightOptions(modelSelect.value, Number(yearSelect.value));
      hardwareOptions.innerHTML = "";
      hardwareGroup.disabled = options.length === 0;
      hardwareHint.textContent = options.length
        ? "Choose the assembly fitted to the vehicle."
        : "Select a vehicle to show its possible factory systems.";
      options.forEach((label, index) => {
        const key = label.toLowerCase().replaceAll(" ", "-");
        hardwareOptions.append(optionRow("radio", label, label, "", "Factory fitted", index === 0));
      });
    }

    function visibleAddons() {
      const model = modelSelect.value;
      const year = Number(yearSelect.value);
      const headlight = selectedHeadlight();
      const keys = [];
      if (model === "Lamborghini Urus SE") return keys;
      if ((model === "S3" || model === "RS3") && year === 2026) keys.push("sfd2");
      if (headlight === "Matrix LED with laser" && !sets.noLaser.has(model)) keys.push("laser");
      if (headlight === "Digital Matrix LED" && sets.laneLight.has(model)) keys.push("laneLight");
      if (sets.maneuver.has(model)) keys.push("maneuverLight");
      if (sets.maneuverAssist.has(model) && year < 2023) keys.push("maneuverAssist");
      return keys;
    }

    function renderAddons() {
      const keys = visibleAddons();
      addonOptions.innerHTML = "";
      addonGroup.disabled = !modelSelect.value;
      addonHint.textContent = !modelSelect.value
        ? "Available functions will appear after the vehicle is identified."
        : keys.length
          ? "These additions match the current vehicle and hardware selection."
          : "No paid additions apply to this combination.";
      keys.forEach((key) => {
        const meta = addonMeta[key];
        addonOptions.append(optionRow("checkbox", key, meta.label, meta.copy, meta.required ? "Required" : money(products[key].price), meta.required, meta.required));
      });
    }

    function visibleIncluded() {
      const model = modelSelect.value;
      const year = Number(yearSelect.value);
      if (!model || sets.noIncluded.has(model)) return [];
      const keys = [];
      if (sets.crossing.has(model)) keys.push("crossing");
      if (model !== "S3" && model !== "RS3") keys.push("singleSide", "sideMarker");
      if (year >= 2020) keys.push("acceleration");
      keys.push("lapTimer");
      return keys;
    }

    function renderIncluded() {
      const keys = visibleIncluded();
      includedOptions.innerHTML = "";
      includedWrap.hidden = keys.length === 0;
      keys.forEach((key) => {
        const meta = includedMeta[key];
        includedOptions.append(optionRow("checkbox", key, meta.label, meta.copy, "Included"));
      });
    }

    function selectedAddonKeys() {
      return [...addonOptions.querySelectorAll("input:checked")].map((input) => input.value);
    }

    function renderSummary() {
      const keys = selectedAddonKeys();
      const addons = keys.reduce((sum, key) => sum + products[key].price, 0);
      $("[data-base-price]").textContent = money(products.base.price);
      $("[data-addon-price]").textContent = money(addons);
      $("[data-total-price]").textContent = money(products.base.price + addons);
      $("[data-summary-title]").textContent = modelSelect.value
        ? `${yearSelect.value || ""} ${modelSelect.value}`.trim()
        : "Start with your vehicle";
    }

    function renderAll() {
      renderRecall();
      renderHardware();
      renderAddons();
      renderIncluded();
      renderSummary();
    }

    function validate() {
      if (!modelSelect.value) return report(modelSelect, "Choose your vehicle model.");
      if (!yearSelect.value) return report(yearSelect, "Choose your model year.");
      if (!selectedHeadlight()) {
        hardwareGroup.scrollIntoView({ behavior: "smooth", block: "center" });
        return false;
      }
      if (!recallStage.hidden && !recallSelect.value) return report(recallSelect, "Confirm the recall status.");
      if (!cableReason.hidden && !cableReason.querySelector("select").value) return report(cableReason.querySelector("select"), "Tell us which cable you have.");
      return true;
    }

    function report(control, message) {
      control.setCustomValidity(message);
      control.reportValidity();
      control.setCustomValidity("");
      control.focus();
      return false;
    }

    async function checkout() {
      if (!validate()) return;
      if (!products.base.id) {
        checkoutNote.textContent = "The core service product has not been assigned in the Shopify Theme Editor.";
        checkoutNote.classList.add("is-error");
        return;
      }

      const addonKeys = selectedAddonKeys();
      const unavailable = addonKeys.find((key) => !products[key].id);
      if (unavailable) {
        checkoutNote.textContent = `${addonMeta[unavailable].label} is not connected to a Shopify product yet.`;
        checkoutNote.classList.add("is-error");
        return;
      }

      const selectedIncluded = [...includedOptions.querySelectorAll("input:checked")]
        .map((input) => includedMeta[input.value].label)
        .join(", ") || "None selected";
      const cable = root.querySelector("input[name='cable']:checked").value;
      const configurationId = `GO-${Date.now().toString(36).toUpperCase()}`;
      const properties = {
        "Vehicle": `${yearSelect.value} ${modelSelect.value}`,
        "Headlight assembly": selectedHeadlight(),
        "Recall status": recallStage.hidden ? "Not applicable" : recallSelect.value,
        "Connection cable": cable === "rental" ? "Loan cable requested" : cableReason.querySelector("select").value,
        "Included session requests": selectedIncluded,
        "_German Orbit configuration": configurationId,
      };
      const items = [
        { id: Number(products.base.id), quantity: 1, properties },
        ...addonKeys.map((key) => ({
          id: Number(products[key].id),
          quantity: 1,
          properties: { "_German Orbit configuration": configurationId, "_Vehicle": `${yearSelect.value} ${modelSelect.value}` },
        })),
      ];

      checkoutButton.disabled = true;
      checkoutButton.textContent = "Preparing checkout…";
      checkoutNote.classList.remove("is-error");

      try {
        const response = await fetch(`${window.Shopify?.routes?.root || "/"}cart/add.js`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ items }),
        });
        if (!response.ok) {
          const result = await response.json().catch(() => ({}));
          throw new Error(result.description || "Shopify could not add this configuration.");
        }
        window.location.assign("/cart");
      } catch (error) {
        checkoutButton.disabled = false;
        checkoutButton.textContent = "Continue to checkout";
        checkoutNote.textContent = error.message || "The checkout could not be prepared. Please try again.";
        checkoutNote.classList.add("is-error");
      }
    }

    modelSelect.addEventListener("change", () => {
      renderYears();
      renderAll();
    });
    yearSelect.addEventListener("change", renderAll);
    hardwareOptions.addEventListener("change", () => {
      renderAddons();
      renderSummary();
    });
    addonOptions.addEventListener("change", renderSummary);
    root.querySelectorAll("input[name='cable']").forEach((input) => {
      input.addEventListener("change", () => {
        cableReason.hidden = input.value !== "own" || !input.checked;
      });
    });
    checkoutButton.addEventListener("click", checkout);

    renderYears();
    renderAll();
  }

  function initAll(container = document) {
    container.querySelectorAll("[data-go-configurator]").forEach(init);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initAll());
  } else {
    initAll();
  }
  document.addEventListener("shopify:section:load", (event) => initAll(event.target));
})();
